import { Dialog } from "@dash/dialog";
import { Button } from "@dash/form";
import { useTheme } from "@emotion/react";
import React from "react";
import { MdDeleteOutline } from "react-icons/md";

import { ReportCellWidth, reportCellWidths } from "@megaron/analytics-contracts";

const widthLabels: Record<ReportCellWidth, string> = {
  full: "Cała",
  half: "1/2",
  third: "1/3",
  quarter: "1/4",
};

export const WidthPicker: React.FC<{ value: ReportCellWidth; onChange: (width: ReportCellWidth) => void }> = ({
  value,
  onChange,
}) => {
  const theme = useTheme();

  return (
    <div
      role="radiogroup"
      aria-label="Szerokość"
      css={{
        display: "flex",
        padding: "3px",
        gap: "2px",
        borderRadius: theme.smallBorderRadius,
        background: theme.colors.primaryLight,
      }}
    >
      {reportCellWidths.map((width) => (
        <button
          key={width}
          type="button"
          role="radio"
          aria-checked={width === value}
          onClick={() => onChange(width)}
          css={{
            border: "none",
            borderRadius: "4px",
            padding: "0.3125rem 0.75rem",
            fontSize: "0.8125rem",
            cursor: "pointer",
            background: width === value ? "white" : "transparent",
            color: width === value ? theme.colors.primary : theme.colors.secondaryText,
            fontWeight: width === value ? 600 : 400,
            boxShadow: width === value ? "0 1px 2px rgba(0, 0, 0, 0.08)" : "none",
          }}
        >
          {widthLabels[width]}
        </button>
      ))}
    </div>
  );
};

export const IconButton: React.FC<{
  title: string;
  onClick: () => void;
  isDisabled?: boolean;
  color?: string;
  className?: string;
  children: React.ReactNode;
}> = ({ title, onClick, isDisabled, color, className, children }) => {
  const theme = useTheme();

  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onClick={onClick}
      disabled={isDisabled}
      className={className}
      css={{
        border: "none",
        background: "transparent",
        color: color ?? theme.colors.secondaryText,
        width: "32px",
        height: "32px",
        borderRadius: theme.smallBorderRadius,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        fontSize: "1.125rem",
        "&:hover:not(:disabled)": { background: theme.colors.primaryLight },
        "&:disabled": { opacity: 0.3, cursor: "default" },
      }}
    >
      {children}
    </button>
  );
};

export const VisibleToggle: React.FC<{ value: boolean; onChange: (visible: boolean) => void }> = ({
  value,
  onChange,
}) => {
  const theme = useTheme();

  return (
    <label
      css={{
        display: "flex",
        alignItems: "center",
        gap: "0.375rem",
        fontSize: "0.8125rem",
        color: theme.colors.secondaryText,
        cursor: "pointer",
        whiteSpace: "nowrap",
      }}
    >
      <input
        type="checkbox"
        checked={value}
        onChange={(e) => onChange(e.target.checked)}
        css={{ accentColor: theme.colors.primary, margin: 0 }}
      />
      Pokaż w raporcie
    </label>
  );
};

export const RemoveCellDialog: React.FC<{ title: string; onClose: () => void; onRemove: () => void }> = ({
  title,
  onClose,
  onRemove,
}) => {
  const theme = useTheme();

  return (
    <Dialog onClose={onClose} placement="center" css={{ width: "400px" }} hideHeader>
      <div css={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        <span css={{ fontSize: "1rem", color: theme.colors.danger, fontWeight: 700 }}>Usuń blok</span>
        <span css={{ fontSize: "0.875rem", color: "black" }}>
          Czy na pewno chcesz usunąć blok{title ? ` „${title}”` : ""}?
        </span>
        <div css={{ display: "flex", gap: "0.625rem", marginLeft: "auto" }}>
          <Button variant="outline" onClick={onClose}>
            Anuluj
          </Button>
          <Button icon={<MdDeleteOutline />} css={{ background: theme.colors.danger }} onClick={onRemove}>
            Usuń
          </Button>
        </div>
      </div>
    </Dialog>
  );
};
